/**
 * @author        tae@zumper.com (Tae Yoon)
 * @copyright     Copyright (c) 2018, Zumper
 * @description   Detail selectors
 */

import { selectResidentQuotesQuotes } from '@zcode-ui/web-zumper-features'
import { selectEntity, selectNow } from '@zcode-ui/web-zumper-global-state'
import { BuildingReviewsSourceNames } from '@zcode/bizos'
import { PropertyFeatures } from '@zcode/enums'
import { createSelector } from '@zcode/memoizers'
import type { MaybeEmptyProperty, State } from '@zcode/state-type'
import { compose } from 'redux'
import { selectRatingsAndReviews } from '../../ratingsAndReviews/selectors'
import { sortByDateAndPrice } from '../helpers'

export const selectDetail = (state: State) => state.detail

export const selectMobileView = compose((detail) => detail.mobileView, selectDetail)

export const selectActiveListings = compose((detail) => detail.activeListings, selectDetail)
export const selectInactiveListings = compose((detail) => detail.inactiveListings, selectDetail)

export const selectIsApplyNowEnabled = (
  state: State,
  floorplanListingId: number | null,
  providedEntity?: MaybeEmptyProperty
) => {
  const entity = providedEntity || selectDetail(state).entity.data
  const features = floorplanListingId
    ? entity?.floorplans?.find(({ listingId }) => listingId === floorplanListingId)?.features
    : entity?.features
  const hasScreening = features?.includes(PropertyFeatures.INSTANT_APPLY_SCREENING)
  const hasApplication = features?.includes(PropertyFeatures.INSTANT_APPLY_APPLICATION)
  return hasScreening && hasApplication
}

export const selectDILAddresses = compose((detail) => detail.dilAddresses, selectDetail)
export const selectDILBuildings = compose((detail) => detail.dilBuildings, selectDetail)

export const selectDetailEntityData = compose((detail) => detail?.entity?.data, selectDetail)
const selectUnits = compose((detail) => detail?.units?.data, selectDetail)
export const selectFloorplans = compose(
  (entityData) => entityData?.floorplans,
  selectDetailEntityData
)

export const selectDetailModalFloorplan = (state: State) => {
  const detail = selectDetail(state)
  const listingId = detail.floorplanModal.selectedFloorplan
  const floorplans = selectFloorplans(state) ?? []

  const selectedFloorplan = floorplans.find((floorplan) => floorplan.listingId === listingId)
  return selectedFloorplan || null
}

const selectSelectedFloorplanUnits = createSelector(
  selectUnits,
  selectDetailModalFloorplan,
  (units, floorplan) =>
    floorplan && units ? units.filter((unit) => unit.listingId === floorplan.listingId) : []
)

export const selectAvailableSelectedFloorplanUnits = createSelector(
  selectNow,
  selectSelectedFloorplanUnits,
  (now, units) => units?.filter((unit) => unit.isActive).sort(sortByDateAndPrice(now))
)

const selectFloorplanModal = compose((detail) => detail.floorplanModal, selectDetail)

export const selectIsFloorplanDetailModalOpen = compose(
  (floorplanModal) => floorplanModal.isOpen,
  selectFloorplanModal
)

export const selectFloorplanDetailModalOpener = compose(
  (floorplanModal) => floorplanModal.opener,
  selectFloorplanModal
)

export const selectLocationSchools = compose((state) => state.locationSchools, selectDetail)

export const selectGalleryUx = compose((state) => state.galleryUx, selectDetail)

export const selectDetailPageLastUpdated = createSelector(
  selectDetail,
  selectEntity,
  selectResidentQuotesQuotes,
  selectRatingsAndReviews,
  (detail, entity, quotes, ratings) => {
    const timestamps: number[] = []
    if (entity?.modifiedOn) {
      timestamps.push(entity.modifiedOn)
      if (entity.floorplans) {
        timestamps.push(
          ...entity.floorplans.map((floorplan) => floorplan.modifiedOn).filter(Boolean)
        )
      }
    }
    const reviews = detail.buildingReviews.data.sources?.[BuildingReviewsSourceNames.GOOGLE].reviews
    if (reviews) {
      timestamps.push(...reviews.map(({ date }) => date).filter(Boolean))
    }
    for (const { data } of detail.mlsData.values()) {
      if (data) {
        timestamps.push(data.modifiedOn)
      }
    }
    if (quotes) {
      for (const { quotedOn } of quotes) {
        timestamps.push(quotedOn)
      }
    }
    for (const { reviewedOn } of ratings.detail.reviews) {
      timestamps.push(reviewedOn)
    }
    if (detail.units.data) {
      timestamps.push(...detail.units.data.map((unit) => unit.modifiedOn).filter(Boolean))
    }
    if (!timestamps.length) {
      return null
    }
    return Math.max(...timestamps)
  }
)
